import React from 'react';
import { Paper, Typography, TextField, Button, Box, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Business as UnionIcon } from '@mui/icons-material';

import { commonButtonStyles } from '../../../../components/styles/common-style';
// Note: Validation functions are now handled by the parent hook

import type { UnionMembership, CooperativeUnion } from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

interface UnionMembershipSectionProps {
  unionMembership: UnionMembership;
  originalUnionMembership?: UnionMembership;
  isEditMode?: boolean;
  onUpdateIndustrialUnion: (value: string) => void;
  onAddCooperativeUnion: () => void;
  onUpdateCooperativeUnion: (index: number, field: keyof CooperativeUnion, value: string) => void;
  onRemoveCooperativeUnion: (index: number) => void;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
}

export const UnionMembershipSection: React.FC<UnionMembershipSectionProps> = ({
  unionMembership,
  originalUnionMembership,
  isEditMode,
  onUpdateIndustrialUnion,
  onAddCooperativeUnion,
  onUpdateCooperativeUnion,
  onRemoveCooperativeUnion,
  getFieldError,
  isFieldValid,
}) => {
  // Note: Validation is now handled by the parent hook to ensure proper touched field checking
  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 2, paddingBottom: '40px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        加盟組合
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 加盟工業組合 */}
        <Box>
          <TextField
            label='加盟工業組合'
            placeholder='加盟工業組合を入力'
            value={unionMembership.industrialUnion || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdateIndustrialUnion(value);
            }}
            error={!isFieldValid('unionMembership.industrialUnion')}
            helperText={getFieldError('unionMembership.industrialUnion')}
            fullWidth
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
                sx: getStylesForEditableField(
                  !!isEditMode,
                  originalUnionMembership?.industrialUnion,
                  unionMembership.industrialUnion
                ),
              },
            }}
          />
        </Box>

        {/* 加盟協同組合 */}
        <Box>
          {unionMembership.cooperativeUnions.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <UnionIcon sx={{ fontSize: 48, mb: 1, color: '#0047BA' }} />
              <Typography variant='body1'>加盟協同組合</Typography>
              <Button
                variant='outlined'
                startIcon={<AddIcon />}
                onClick={onAddCooperativeUnion}
                disabled={unionMembership.cooperativeUnions.length >= 10}
                sx={{
                  ...commonButtonStyles,
                  mt: 2,
                  px: 3,
                  py: 1,
                }}
              >
                組合追加
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {unionMembership.cooperativeUnions.map((union, index) => {
                const originalUnionItem =
                  union.index !== undefined
                    ? originalUnionMembership?.cooperativeUnions?.[union.index]
                    : undefined;
                return (
                  <Paper key={union.id} variant='outlined' sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        label={`加盟協同組合${index + 1}`}
                        placeholder='加盟協同組合を入力'
                        value={union.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          onUpdateCooperativeUnion(index, 'name', value);
                        }}
                        error={!isFieldValid(`unionMembership.cooperativeUnions.${index}.name`)}
                        helperText={getFieldError(
                          `unionMembership.cooperativeUnions.${index}.name`
                        )}
                        fullWidth
                        required
                        slotProps={{
                          input: {
                            inputProps: { maxLength: 50 },
                            sx: getStylesForEditableField(
                              !!isEditMode,
                              originalUnionItem?.name,
                              union.name
                            ),
                          },
                        }}
                      />
                      <IconButton
                        color='error'
                        onClick={() => onRemoveCooperativeUnion(index)}
                        sx={{ flexShrink: 0, color: '#FF8985' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          {/* Add Cooperative Union Button (bottom) */}
          {unionMembership.cooperativeUnions.length > 0 &&
            unionMembership.cooperativeUnions.length < 10 && (
              <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
                <Button
                  variant='outlined'
                  startIcon={<AddIcon />}
                  onClick={onAddCooperativeUnion}
                  sx={{
                    ...commonButtonStyles,
                    px: 3,
                    py: 1,
                  }}
                >
                  組合追加 ({unionMembership.cooperativeUnions.length}/10)
                </Button>
              </Box>
            )}
        </Box>
      </Box>
    </PaperBoxStyles>
  );
};
